import React, { useState } from 'react';
import { ResponsiveContainer } from 'recharts';
import { CommonChartProps, ChartType } from './types';
import { useNormalizedSeries } from './hooks/useNormalizedSeries';
import { useCommonChartElements } from './hooks/useCommonChartElements';
import { cn } from '../../../utils/cn';
import { useChartDataWithDelta } from './hooks/useChartDataWithDelta';
import {
  defaultCartesianElementsConfig,
  defaultDeltaConfig,
} from './chart-config/defaults';
import { ChartColorPalette } from './chart-config/colors';

/**
 * Base props that any chart component should support for compatibility
 * with the BaseChart wrapper.
 */
type BaseChartComponentProps<TData> = {
  data?: TData[];
  width?: number | `${number}%` | undefined;
  height?: number | `${number}%` | undefined;
  children?: React.ReactNode | React.ReactNode[];
};

/**
 * The merged ChartComponent props
 */
type MergedChartComponentProps<TData, TChartProps> = Omit<
  Partial<TChartProps>,
  'data' | 'children'
> &
  BaseChartComponentProps<TData>;

/**
 * Props for the BaseChart component.
 *
 * @template TData - The type of data each data point represents.
 * @template TChartProps - The props specific to the ChartComponent.
 */
export interface BaseChartProps<
  TData,
  TChartProps extends BaseChartComponentProps<TData>,
  TSeriesConfig,
> extends CommonChartProps<TData, TChartProps, TSeriesConfig> {
  /**
   * The chart component to render (e.g., LineChart, BarChart).
   */
  ChartComponent: React.ComponentType<
    MergedChartComponentProps<TData, TChartProps>
  >;

  /**
   * Function to render the normalized data series using Chart components like Line, Bar, Area, etc.
   *
   * @param normalizedSeries - The normalized series data.
   * @returns A React node representing the rendered series.
   */
  renderSeries: ({
    normalizedSeries,
    colors,
    processedData,
    activeIndex,
    setActiveIndex,
  }: {
    normalizedSeries: ReturnType<
      typeof useNormalizedSeries<TData, TChartProps, TSeriesConfig>
    >;
    colors: string[];
    processedData: TData[];
    activeIndex: number | null;
    setActiveIndex: (index: number | null) => void;
  }) => React.ReactNode;

  /**
   * Additional props specific to the ChartComponent.
   */
  chartProps?: Partial<TChartProps>;
  chartType: ChartType;
}

/**
 * BaseChart is a reusable component for rendering charts available in Recharts.
 *
 * @template TData - The type of data each data point represents.
 * @template TChartProps - The props specific to the ChartComponent.
 *
 * @param props - The properties for the BaseChart component.
 * @returns A React element representing the chart.
 */
export const BaseChart = <
  TData,
  TChartProps extends BaseChartComponentProps<TData>,
  TSeriesConfig,
>({
  data,
  series,
  ChartComponent,
  renderSeries,
  chartProps = {},
  className,
  deltaConfig = defaultDeltaConfig,
  colors = ChartColorPalette.default,
  chartType,
  seriesProps,
  ...restProps
}: BaseChartProps<TData, TChartProps, TSeriesConfig>) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  /**
   * Normalizes the series data using the useNormalizedSeries hook.
   */
  const normalizedSeries = useNormalizedSeries(
    series,
    chartType,
    seriesProps,
    colors,
  );

  /**
   * Computes and appends delta information to the data using the custom hook.
   */
  const processedData = useChartDataWithDelta(
    data,
    normalizedSeries,
    deltaConfig,
  );

  /**
   * Merges the default configuration with any additional properties.
   */
  const finalConfig = { ...defaultCartesianElementsConfig, ...restProps };

  /**
   * Generates the necessary chart elements based on the configuration and props.
   */
  const chartElements = useCommonChartElements({
    data: processedData,
    ...finalConfig,
    ...restProps,
    chartType,
    activeIndex,
    setActiveIndex,
  });

  return (
    <ResponsiveContainer
      // This is required to make the width and height work with className
      width={chartProps.width || '100%'}
      height={chartProps.height || '100%'}
      className={cn('h-[400px] w-full', className)}
    >
      <ChartComponent
        {...{
          ...chartProps,
        }}
        data={processedData}
      >
        {chartElements}
        {renderSeries({
          normalizedSeries,
          colors,
          processedData,
          activeIndex,
          setActiveIndex,
        })}
      </ChartComponent>
    </ResponsiveContainer>
  );
};
